import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import SEO from "../components/SEO";
import { locations, locationsByZone, zoneOrder } from "../data/locations";

const SITE = "https://kandkassociates.com";

export default function Locations() {
  const byZone = locationsByZone();

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ItemList",
        name: "Locations served by K & K Associates",
        itemListElement: locations.map((l, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: `${SITE}/locations/${l.slug}`,
          name: `Lawyers in ${l.name}`,
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
          { "@type": "ListItem", position: 2, name: "Locations" },
        ],
      },
    ],
  };

  return (
    <>
      <SEO
        title="Locations We Serve — Lawyers Across Delhi NCR"
        description="K & K Associates serves clients across 50 Delhi NCR locations — Defence Colony, Saket, Hauz Khas, Connaught Place, Dwarka, Rohini, Karkardooma, Lajpat Nagar, Noida, Gurugram, Faridabad, Ghaziabad and more. Find experienced lawyers and advocates near you."
        canonical={`${SITE}/locations`}
        keywords={["lawyers in Delhi NCR", "advocates near me Delhi", "law firm locations Delhi", "best lawyers Delhi NCR", "legal services Delhi localities"]}
        schema={schema}
      />

      {/* Hero */}
      <section className="pt-28 md:pt-40 pb-14 md:pb-20 bg-paper border-b border-rule">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <p className="kicker mb-6">Locations We Serve</p>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <h1 className="lg:col-span-8 font-display text-4xl sm:text-5xl md:text-6xl xl:text-7xl leading-[1] sm:leading-[0.98] text-ink">
              Lawyers across <span className="italic text-brand">Delhi NCR.</span>
            </h1>
            <p className="lg:col-span-4 font-serif text-lg text-ink/70 leading-relaxed">
              From Defence Colony to Dwarka, Rohini to Noida — K & K Associates provides full-service legal representation to clients across 100 localities in Delhi and the National Capital Region.
            </p>
          </div>
        </div>
      </section>

      {/* Zones */}
      <section className="bg-bone py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 space-y-12 md:space-y-16">
          {zoneOrder.map((zone) => (
            <div key={zone} data-testid={`location-zone-${zone.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="w-5 h-5 text-brand" strokeWidth={1.5} />
                <h2 className="font-display text-2xl md:text-3xl text-ink">{zone}</h2>
                <span className="kicker text-ink/40">{byZone[zone].length} areas</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-rule">
                {byZone[zone].map((l) => (
                  <Link
                    key={l.slug}
                    to={`/locations/${l.slug}`}
                    className="group border-r border-b border-rule p-6 bg-paper hover:bg-bone transition-colors flex items-center justify-between gap-3"
                    data-testid={`location-link-${l.slug}`}
                  >
                    <span>
                      <span className="font-display text-lg text-ink group-hover:text-brand transition-colors">{l.name}</span>
                      <span className="block text-xs text-ink/40 mt-1 tracking-wider">{l.pincode}</span>
                    </span>
                    <ArrowRight className="w-4 h-4 text-ink/30 group-hover:text-brand group-hover:translate-x-1 transition-all shrink-0" strokeWidth={1.5} />
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20 bg-paper">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-display text-3xl md:text-5xl leading-tight text-ink">
            Don't see your area listed?
          </h2>
          <p className="mt-6 font-serif text-xl text-ink/70 max-w-2xl mx-auto">
            We serve all of Delhi NCR and act pan-India through a vetted local-counsel network. Tell us where you are.
          </p>
          <div className="mt-10">
            <Link to="/contact" className="btn-primary" data-testid="locations-cta-contact">
              Speak with a Partner <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
